import React, { createContext, useState, useContext, useCallback, useEffect, useRef } from "react";
import * as auth from "../../services/AuthService";
import Fetch from "../Fetch";
import jwt_decode from "jwt-decode";
const AuthContext = createContext({});

// This checks the token's shape and dates only. The API verifies its signature.
const getValidTokenUser = (token) => {
  try {
    if (typeof token !== "string" || !/^[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+$/.test(token)) {
      return null;
    }
    const decoded = jwt_decode(token);
    const now = Date.now() / 1000;
    if (!Number.isFinite(decoded?.exp) || decoded.exp <= now) return null;
    if (decoded.nbf !== undefined && (!Number.isFinite(decoded.nbf) || decoded.nbf > now)) return null;
    return decoded;
  } catch {
    return null;
  }
};

const getUserRole = async () => {
  try {
    const response = await Fetch.get("/user/user-role");
    return response.data
  } catch {
    return null;
  }
}

const needToValidateContract = () => {
  try {
    const token = localStorage.getItem("token");
    const decoded = jwt_decode(token);
    return decoded?.validateContract;
  } catch (e) {
    return false;
  }
}

const AuthProvider = ({ children }) => {
  const [session, setSession] = useState(null);
  const [isCheckingSession, setIsCheckingSession] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const sessionVersion = useRef(0);

  const signOut = useCallback(() => {
    // Invalidate pending requests so they cannot restore a signed-out session.
    sessionVersion.current += 1;
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    setSession(null);
    setIsCheckingSession(false);
    setIsLoading(false);
  }, []);

  useEffect(() => {
    const restoreSession = async () => {
      const version = ++sessionVersion.current;
      const token = localStorage.getItem("token");
      setSession(null);
      setIsCheckingSession(true);
      if (!getValidTokenUser(token)) {
        signOut();
        return;
      }

      try {
        // Use the exact token being checked, without redirecting from an interceptor.
        const response = await Fetch.createInstanceToken(token).get("/user/user-role");
        if (version !== sessionVersion.current) return;
        const user = getValidTokenUser(token);
        if (response.status !== 200 || !user || localStorage.getItem("token") !== token) {
          signOut();
          return;
        }
        setSession({ token, user });
        setIsCheckingSession(false);
      } catch {
        if (version === sessionVersion.current) signOut();
      }
    };

    const handleStorage = (event) => {
      if (event.storageArea === localStorage && (event.key === "token" || event.key === null)) {
        restoreSession();
      }
    };
    restoreSession();
    window.addEventListener("storage", handleStorage);
    return () => {
      sessionVersion.current += 1;
      window.removeEventListener("storage", handleStorage);
    };
  }, [signOut]);

  useEffect(() => {
    if (!session) return;
    let timeout;
    const checkExpiration = () => {
      clearTimeout(timeout);
      const user = getValidTokenUser(session.token);
      if (!user || localStorage.getItem("token") !== session.token) {
        signOut();
        return;
      }
      // Browsers limit timeout delays to a signed 32-bit integer.
      timeout = setTimeout(checkExpiration, Math.min(user.exp * 1000 - Date.now(), 2147483647));
    };
    checkExpiration();
    window.addEventListener("focus", checkExpiration);
    document.addEventListener("visibilitychange", checkExpiration);
    return () => {
      clearTimeout(timeout);
      window.removeEventListener("focus", checkExpiration);
      document.removeEventListener("visibilitychange", checkExpiration);
    };
  }, [session, signOut]);

  async function signIn({ email, password, reCaptcha }, callback) {
    const version = ++sessionVersion.current;
    setSession(null);
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    setIsCheckingSession(false);
    setIsLoading(true);
    try {
      const response = await auth.signIn({ email, password, reCaptcha });
      if (version !== sessionVersion.current) return;
      const user = getValidTokenUser(response?.token);
      if (!user) {
        signOut();
        callback?.(null);
        return;
      }
      // A successful login response is the server confirmation for a new session.
      localStorage.setItem("token", response.token);
      setSession({ token: response.token, user });
      setIsLoading(false);
      callback?.(user);
    } catch {
      if (version === sessionVersion.current) {
        signOut();
        callback?.(null);
      }
    }
  }

  return (
    <>
      <AuthContext.Provider
        value={{ isSigned: !!session, user: session?.user ?? null, isLoading, isCheckingSession, signIn, signOut }}
      >
        {children}
      </AuthContext.Provider>
    </>
  );
};

function useAuth() {
  return useContext(AuthContext);
}

export { AuthProvider, useAuth, getUserRole, needToValidateContract };
